import { useState } from 'react';
import { Routes, Route, Navigate, Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, UnauthorizedError } from './lib/api.js';
import LoginPage from './pages/Login.jsx';
import MapPage from './pages/Map.jsx';
import ListPage from './pages/List.jsx';
import CampaignPage from './pages/Campaign.jsx';
import BusinessDetailPage from './pages/BusinessDetail.jsx';
import ProfilePage from './pages/Profile.jsx';
import AdminPage from './pages/Admin.jsx';
import IdeateDialog from './components/IdeateDialog.jsx';

/**
 * Fetch current user. If the cookie is missing/expired but we have a stored
 * invite token in localStorage, silently re-exchange it for a new cookie and
 * retry — this keeps board members logged in indefinitely on devices where
 * they've clicked the invite link at least once.
 */
async function fetchMeWithFallback() {
  try {
    return await api.me();
  } catch (err) {
    if (!(err instanceof UnauthorizedError)) throw err;

    let stored = null;
    try {
      stored = localStorage.getItem('ccf_invite_token');
    } catch {}
    if (!stored) throw err;

    try {
      await api.login(stored);
      return await api.me();
    } catch (reauthErr) {
      // Stored token is also invalid — clear it so we stop retrying
      try {
        localStorage.removeItem('ccf_invite_token');
      } catch {}
      throw reauthErr;
    }
  }
}

export default function App() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMeWithFallback,
    retry: false,
  });

  if (isLoading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  const isAuthed = !!data?.user && !(error instanceof UnauthorizedError);

  if (!isAuthed) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<Layout user={data.user} />}>
        <Route path="/" element={<Navigate to="/map" replace />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/list" element={<ListPage />} />
        <Route path="/campaign" element={<CampaignPage />} />
        <Route path="/business/:id" element={<BusinessDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {data.user.role === 'admin' && <Route path="/admin" element={<AdminPage />} />}
      </Route>
    </Routes>
  );
}

function Layout({ user }) {
  const navigate = useNavigate();
  const [ideateOpen, setIdeateOpen] = useState(false);

  async function handleLogout() {
    await api.logout();
    try {
      localStorage.removeItem('ccf_invite_token');
    } catch {}
    navigate('/login');
    window.location.reload();
  }

  return (
    <div className="h-full flex flex-col">
      <header className="bg-ccf-primary text-white px-6 py-3 flex items-center justify-between shadow">
        <div className="flex items-center gap-6">
          <h1 className="font-bold text-lg">CCF Board Dashboard</h1>
          <nav className="flex gap-4 text-sm">
            <Link to="/map" className="hover:underline">Map</Link>
            <Link to="/list" className="hover:underline">List</Link>
            <Link to="/campaign" className="hover:underline">Campaign</Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="hover:underline">Admin</Link>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <button
            onClick={() => setIdeateOpen(true)}
            className="bg-ccf-accent text-white rounded px-3 py-1.5 hover:bg-emerald-700 text-xs font-semibold flex items-center gap-1.5"
            title="Ask AI to find prospects"
          >
            <span>✨</span>
            <span>Ask AI</span>
          </button>
          <Link to="/profile" className="hover:underline">{user?.name}</Link>
          <button onClick={handleLogout} className="text-xs opacity-70 hover:opacity-100">
            Sign out
          </button>
        </div>
      </header>
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
      <IdeateDialog open={ideateOpen} onClose={() => setIdeateOpen(false)} />
    </div>
  );
}
